import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface RefOption {
  id: number;
  type_name?: string;
  provider_name?: string;
}

interface BenefitPlan {
  id: number;
  employee_benefit_type_id: number;
  employee_benefit_provider_id: number;
  plan_code: string;
  plan_name: string;
  effective_from: string;
  effective_to: string | null;
  active_flag: boolean;
  type: RefOption;
  provider: RefOption;
}

interface BenefitPlanForm {
  employee_benefit_type_id: number | '';
  employee_benefit_provider_id: number | '';
  plan_code: string;
  plan_name: string;
  effective_from: string;
  effective_to: string;
  active_flag: boolean;
}

const emptyForm: BenefitPlanForm = {
  employee_benefit_type_id: '',
  employee_benefit_provider_id: '',
  plan_code: '',
  plan_name: '',
  effective_from: '',
  effective_to: '',
  active_flag: true,
};

export function BenefitPlans() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<BenefitPlan[] | null>(null);
  const [types, setTypes] = useState<RefOption[]>([]);
  const [providers, setProviders] = useState<RefOption[]>([]);
  const [editing, setEditing] = useState<'new' | BenefitPlan | null>(null);
  const [form, setForm] = useState<BenefitPlanForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<BenefitPlan[]>('/employee-benefit-plans').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<RefOption[]>('/employee-benefit-types').then((res) => setTypes(res.data));
    api.get<RefOption[]>('/employee-benefit-providers').then((res) => setProviders(res.data));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: BenefitPlan) => {
    setForm({
      employee_benefit_type_id: item.employee_benefit_type_id,
      employee_benefit_provider_id: item.employee_benefit_provider_id,
      plan_code: item.plan_code,
      plan_name: item.plan_name,
      effective_from: item.effective_from,
      effective_to: item.effective_to ?? '',
      active_flag: item.active_flag,
    });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof BenefitPlanForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/employee-benefit-plans', form);
      else if (editing) await api.put(`/employee-benefit-plans/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: BenefitPlan) => {
    if (!(await confirmDelete(`Delete "${item.plan_name}"?`))) return;
    try {
      await api.delete(`/employee-benefit-plans/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Benefit Plans</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Provider</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.plan_code}</td>
                  <td>{item.plan_name}</td>
                  <td>{item.type?.type_name}</td>
                  <td className="muted">{item.provider?.provider_name}</td>
                  <td>
                    <span className={`badge ${item.active_flag ? 'badge-active' : 'badge-cancelled'}`}>
                      {item.active_flag ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="right">
                    <IconButton icon={IconView} label="View" to={`/system/benefit-plans/${item.id}`} />
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted center">
                    No benefit plans yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add benefit plan' : 'Edit benefit plan'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Code *
              <input value={form.plan_code} onChange={set('plan_code')} required />
              {errors.plan_code && <small className="error">{errors.plan_code[0]}</small>}
            </label>
            <label>
              Name *
              <input value={form.plan_name} onChange={set('plan_name')} required />
              {errors.plan_name && <small className="error">{errors.plan_name[0]}</small>}
            </label>
            <label>
              Type *
              <select value={form.employee_benefit_type_id} onChange={set('employee_benefit_type_id')} required>
                <option value="">—</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.type_name}
                  </option>
                ))}
              </select>
              {errors.employee_benefit_type_id && <small className="error">{errors.employee_benefit_type_id[0]}</small>}
            </label>
            <label>
              Provider *
              <select value={form.employee_benefit_provider_id} onChange={set('employee_benefit_provider_id')} required>
                <option value="">—</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.provider_name}
                  </option>
                ))}
              </select>
              {errors.employee_benefit_provider_id && <small className="error">{errors.employee_benefit_provider_id[0]}</small>}
            </label>
            <label>
              Effective From *
              <input type="date" value={form.effective_from} onChange={set('effective_from')} required />
              {errors.effective_from && <small className="error">{errors.effective_from[0]}</small>}
            </label>
            <label>
              Effective To
              <input type="date" value={form.effective_to} onChange={set('effective_to')} />
              {errors.effective_to && <small className="error">{errors.effective_to[0]}</small>}
            </label>
            <label className="checkbox-inline span-2">
              <input
                type="checkbox"
                checked={form.active_flag}
                onChange={(e) => setForm({ ...form, active_flag: e.target.checked })}
              />
              Active
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
